/**
 * Kassakoopjes (snippets/kassakoopjes.liquid): impulse deals in the cart.
 *
 * "Toevoegen" adds 1 of the offer product (/cart/add.js, line property `_kassakoopje` for
 * attribution: the card's data-line-property, `1` for the standard deal, `kabel` for the cable) and refreshes the cart page and drawer through the theme's own
 * `theme:cartchanged` event (Section Rendering API + merge), so the page does not reload.
 * The offer product is a hidden product with its own (deal) price; no discount is involved.
 *
 * After every render it:
 * - applies the guard the snippet rendered (data-kassakoopjes-fix): an offer line without its
 *   trigger in the cart is removed, a second line or a quantity above 1 is cut back, with a
 *   short note above the deals; and
 * - records which deals the cart was eligible for, which were visible (A/B test variant) and
 *   which were added, as hidden cart attributes that end up on the order:
 *   _kassakoopje_eligible, _kassakoopje_shown, _kassakoopje_added (comma separated offer SKUs).
 *
 * "Nee, dankjewel" closes a card for the session (_kassakoopje_dismissed records it).
 *
 * Offer events for SP Utilities (assets/offer-events.js, offer from the card's data-spu-offer:
 * 'kassakoopje' for the standard deal, 'kassakoopje_kabel' for the cable): 'shown' once a
 * card is really on screen, 'accept' after the deal is in the cart, 'dismiss' on
 * "Nee, dankjewel" and 'reopen' on the text button.
 */
(function () {
  if (window.__senifoneKassakoopjesInitialized) return;
  window.__senifoneKassakoopjesInitialized = true;

  var ATTRIBUTE_ELIGIBLE = '_kassakoopje_eligible';
  var ATTRIBUTE_SHOWN = '_kassakoopje_shown';
  var ATTRIBUTE_ADDED = '_kassakoopje_added';
  var liveRegion = null;
  var syncTimer = null;
  var syncing = false;
  var attemptedFixes = {};

  function routeRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || '/';
  }

  function request(path, body) {
    var options = {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    };
    if (body) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    return fetch(routeRoot() + path, options).then(function (response) {
      return response.json().catch(function () {
        return {};
      }).then(function (data) {
        if (!response.ok) {
          var error = new Error(data.description || data.message || 'HTTP ' + response.status);
          error.status = response.status;
          throw error;
        }
        return data;
      });
    });
  }

  function splitList(value) {
    return String(value || '')
      .split(',')
      .map(function (part) {
        return part.trim();
      })
      .filter(Boolean);
  }

  function withValue(list, value) {
    var values = splitList(list);
    if (values.indexOf(value) === -1) values.push(value);
    return values.join(',');
  }

  function announce(message) {
    if (!message) return;
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }
    // Clear first so the same message is announced again on a second attempt.
    liveRegion.textContent = '';
    setTimeout(function () {
      liveRegion.textContent = message;
    }, 50);
  }

  function offerName(card) {
    return card.dataset.spuOffer || 'kassakoopje';
  }

  // Offer events (assets/offer-events.js); measurement never breaks the card.
  function track(action, card) {
    try {
      if (window.SenifoneOfferEvents) window.SenifoneOfferEvents.publish(offerName(card), action, card);
    } catch (error) {
      // Measurement only.
    }
  }

  // The card steps back for the cart upgrade card or an earlier deal (one offer at a time):
  // data-kassakoopje-yield lists their A/B classes, space separated.
  function yields(card) {
    var classes = document.documentElement.classList;
    return String(card.dataset.kassakoopjeYield || '')
      .split(' ')
      .some(function (name) {
        return name && classes.contains(name);
      });
  }

  // The card is meant for this visitor: its A/B class (if any) is on <html> and no other offer
  // shows instead.
  function isActive(card) {
    var abClass = card.dataset.kassakoopjeAb;
    if (abClass && !document.documentElement.classList.contains(abClass)) return false;
    return !yields(card);
  }

  // Every deal is measured under its own offer name; register each name once, also for a card
  // that first appears after a cart re-render.
  var watchedOffers = {};

  function watchOffers() {
    if (!window.SenifoneOfferEvents) return;
    document.querySelectorAll('[data-kassakoopje]').forEach(function (card) {
      var offer = offerName(card);
      if (watchedOffers[offer]) return;
      watchedOffers[offer] = true;
      var selector = '[data-kassakoopje][data-spu-offer="' + offer + '"]';
      if (offer === 'kassakoopje') selector = '[data-kassakoopje]:not([data-spu-offer]), ' + selector;
      window.SenifoneOfferEvents.watch(offer, selector, isActive);
    });
  }

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false }));
  }

  function isVisible(el) {
    return !!(el && el.offsetParent !== null);
  }

  // After the refresh the card (and the focused button) is gone; move focus to the new line.
  function focusLine(lineKey) {
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      var selector = '[data-merge-list-item="item-key:' + lineKey + '"] .cart-item__description a.name';
      var target = null;
      document.querySelectorAll(selector).forEach(function (el) {
        if (!target && isVisible(el)) target = el;
      });
      if (target) {
        clearInterval(timer);
        if (!document.activeElement || document.activeElement === document.body) {
          target.focus();
        }
      } else if (attempts > 40) {
        clearInterval(timer);
      }
    }, 100);
  }

  function setBusy(card, button, busy) {
    card.dataset.busy = busy ? 'true' : 'false';
    card.classList.toggle('is-loading', busy);
    button.disabled = busy;
    if (busy) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  function add(card, button) {
    var variantId = parseInt(card.dataset.variantId, 10);
    var sku = card.dataset.sku;
    var errorEl = card.querySelector('[data-kassakoopje-error]');
    var lineKey = '';

    if (!variantId) return;

    setBusy(card, button, true);
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    announce(card.dataset.addingMessage);

    var properties = { _kassakoopje: card.dataset.lineProperty || '1' };
    request('cart/add.js', { items: [{ id: variantId, quantity: 1, properties: properties }] })
      .then(function (response) {
        if (response && response.items && response.items[0]) lineKey = response.items[0].key;
        return request('cart.js').then(function (cart) {
          var attributes = {};
          attributes[ATTRIBUTE_ADDED] = withValue((cart.attributes || {})[ATTRIBUTE_ADDED], sku);
          return request('cart/update.js', { attributes: attributes });
        }).catch(function (error) {
          // The product is in the cart; only the measurement attribute is missing.
          console.warn('[Kassakoopjes] Could not record the add:', error);
        });
      })
      .then(function () {
        track('accept', card);
        refreshCart();
        announce(card.dataset.successMessage);
        if (lineKey) focusLine(lineKey);
      })
      .catch(function (error) {
        console.error('[Kassakoopjes] Add failed:', error);
        setBusy(card, button, false);
        if (errorEl) {
          errorEl.textContent = card.dataset.errorMessage || '';
          errorEl.hidden = false;
        } else {
          announce(card.dataset.errorMessage);
        }
      });
  }

  function showNote(wrapper, message) {
    if (!wrapper || !message) return;
    // Outside the wrapper: the cart refresh replaces the wrapper's content.
    var note = wrapper.previousElementSibling;
    if (!note || !note.hasAttribute('data-kassakoopjes-note')) {
      note = document.createElement('p');
      note.className = 'kassakoopjes-note';
      note.setAttribute('data-kassakoopjes-note', '');
      note.setAttribute('role', 'status');
      wrapper.parentNode.insertBefore(note, wrapper);
    }
    note.textContent = message;
  }

  // The guard: set offer lines that break the rules to the quantity the snippet asked for.
  function applyFixes() {
    var fixes = [];
    document.querySelectorAll('[data-kassakoopjes-fix]').forEach(function (el) {
      var id = el.dataset.lineKey;
      var quantity = parseInt(el.dataset.quantity, 10);
      var attempt = id + ':' + quantity;
      if (!id || isNaN(quantity) || attemptedFixes[attempt]) return;
      attemptedFixes[attempt] = true;
      fixes.push({
        id: id,
        quantity: quantity,
        reason: el.dataset.reason,
        message: el.dataset.message,
        wrapper: el.closest('[data-merge="kassakoopjes"]')
      });
    });
    if (!fixes.length) return Promise.resolve(false);

    return fixes
      .reduce(function (promise, fix) {
        return promise.then(function () {
          return request('cart/change.js', { id: fix.id, quantity: fix.quantity });
        });
      }, Promise.resolve())
      .catch(function (error) {
        console.warn('[Kassakoopjes] Guard could not update the cart:', error);
      })
      .then(function () {
        fixes.forEach(function (fix) {
          var message = fix.message || (fix.wrapper && fix.wrapper.getAttribute(fix.reason === 'removed' ? 'data-removed-message' : 'data-clamped-message'));
          showNote(fix.wrapper, message);
          announce(message);
        });
        refreshCart();
        return true;
      });
  }

  // Applies the guard, then records the eligible/shown attributes for the rendered deals.
  function sync() {
    if (syncing) return;
    syncing = true;
    applyFixes()
      .then(function (fixed) {
        syncing = false;
        // A fix re-renders the cart; the attributes are recorded after that render.
        if (!fixed) recordAttributes();
      });
  }

  function recordAttributes() {
    if (syncing) return;
    var eligible = [];
    var shown = [];
    document.querySelectorAll('[data-kassakoopje]').forEach(function (card) {
      var sku = card.dataset.sku;
      if (!sku) return;
      // The cart upgrade card or an earlier deal shows instead (one offer at a time): not
      // eligible, not shown.
      if (yields(card)) return;
      if (eligible.indexOf(sku) === -1) eligible.push(sku);
      if (isVisible(card) && shown.indexOf(sku) === -1) shown.push(sku);
    });
    if (!eligible.length) return;

    syncing = true;
    request('cart.js')
      .then(function (cart) {
        var attributes = {};
        var current = cart.attributes || {};
        var changed = false;
        [[ATTRIBUTE_ELIGIBLE, eligible], [ATTRIBUTE_SHOWN, shown]].forEach(function (pair) {
          var value = current[pair[0]] || '';
          pair[1].forEach(function (sku) {
            value = withValue(value, sku);
          });
          if (value !== (current[pair[0]] || '')) {
            attributes[pair[0]] = value;
            changed = true;
          }
        });
        if (changed) return request('cart/update.js', { attributes: attributes });
        return null;
      })
      .catch(function (error) {
        console.warn('[Kassakoopjes] Cart sync failed:', error);
      })
      .then(function () {
        syncing = false;
      });
  }

  // The cart refresh has no "done" event; sync shortly after it settles.
  function scheduleSync(delay) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, delay);
  }

  // "Nee, dankjewel" closes the card to a small text button that opens it again. The closed
  // state is kept per browser session (sessionStorage; without storage only for this page), so
  // the card stays closed after a reload or a cart re-render. Closing is recorded as the cart
  // attribute _kassakoopje_dismissed, next to the shown/added attributes.
  var collapsedInMemory = {};

  function isDismissed(key) {
    if (!key) return false;
    try {
      var stored = window.sessionStorage.getItem(key);
      if (stored !== null) return stored === '1';
    } catch (error) {
      // No storage (private mode, blocked): fall back to this page's memory.
    }
    return collapsedInMemory[key] === true;
  }

  function rememberDismissed(key, dismissed) {
    if (!key) return;
    collapsedInMemory[key] = dismissed;
    try {
      if (dismissed) {
        window.sessionStorage.setItem(key, '1');
      } else {
        window.sessionStorage.removeItem(key);
      }
    } catch (error) {
      // Kept in memory only.
    }
  }

  function restoreCollapsed() {
    document.querySelectorAll('[data-kassakoopje][data-dismiss-key]').forEach(function (card) {
      card.classList.toggle('is-collapsed', isDismissed(card.dataset.dismissKey));
    });
  }

  function recordDismissed(sku) {
    if (!sku) return;
    request('cart.js')
      .then(function (cart) {
        var current = (cart.attributes || {})['_kassakoopje_dismissed'] || '';
        var value = withValue(current, sku);
        if (value === current) return null;
        var attributes = {};
        attributes['_kassakoopje_dismissed'] = value;
        return request('cart/update.js', { attributes: attributes });
      })
      .catch(function (error) {
        console.warn('[Kassakoopjes] Could not record the dismiss:', error);
      });
  }

  function setCollapsed(card, collapsed) {
    card.classList.toggle('is-collapsed', collapsed);
    rememberDismissed(card.dataset.dismissKey, collapsed);
    var target = collapsed
      ? card.querySelector('[data-kassakoopje-reopen]')
      : card.querySelector('.kassakoopje__panel');
    if (target) target.focus();
    if (collapsed) recordDismissed(card.dataset.sku);
    track(collapsed ? 'dismiss' : 'reopen', card);
    if (!collapsed && window.SenifoneOfferEvents) window.SenifoneOfferEvents.check();
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-kassakoopje-decline], [data-kassakoopje-reopen]');
    if (!toggle) return;
    var card = toggle.closest('[data-kassakoopje]');
    if (!card) return;
    event.preventDefault();
    setCollapsed(card, toggle.hasAttribute('data-kassakoopje-decline'));
  });

  // A cart re-render replaces the card; close it again before it is painted.
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i += 1) {
        var nodes = records[i].addedNodes;
        for (var j = 0; j < nodes.length; j += 1) {
          var node = nodes[j];
          if (node.nodeType === 1 && (node.matches('[data-kassakoopje]') || node.querySelector('[data-kassakoopje]'))) {
            restoreCollapsed();
            watchOffers();
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
  restoreCollapsed();

  watchOffers();

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-kassakoopje-button]');
    if (!button) return;
    var card = button.closest('[data-kassakoopje]');
    if (!card || card.dataset.busy === 'true') return;
    event.preventDefault();
    add(card, button);
  });

  document.addEventListener('theme:cartchanged', function () {
    scheduleSync(1500);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scheduleSync(300);
    });
  } else {
    scheduleSync(300);
  }
})();
